import React, { useState } from 'react';
import { Filter, Download, FileText, Calendar } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { mockEquipmentModels, mockBrochures, handleBrochureDownload } from '../mock';
import { useToast } from '../hooks/use-toast';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';

const ProductsPage = () => {
  const { toast } = useToast();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedModel, setSelectedModel] = useState(null);

  const categories = [
    { id: 'all', name: 'All Equipment', key: 'all' },
    { id: 'ct', name: 'CT Scanners', key: 'ct' },
    { id: 'mri', name: 'MRI Systems', key: 'mri' },
    { id: 'cathlab', name: 'Cath Labs', key: 'cathlab' },
    { id: 'petct', name: 'PET CT', key: 'petct' }
  ];

  const getAllModels = () => {
    return [
      ...mockEquipmentModels.ct,
      ...mockEquipmentModels.mri,
      ...mockEquipmentModels.cathlab,
      ...mockEquipmentModels.petct
    ];
  };

  const getFilteredModels = () => {
    if (selectedCategory === 'all') {
      return getAllModels();
    }
    return mockEquipmentModels[selectedCategory] || [];
  };

  const handleDownloadBrochure = (brochureId, title) => {
    const result = handleBrochureDownload(brochureId);
    toast({
      title: 'Download Started',
      description: `${title} is being downloaded. In production, the PDF would be downloaded.`,
      duration: 3000,
    });
  };

  const handleInquiry = (model) => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = '/#contact';
    }
  };

  const filteredModels = getFilteredModels();

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-64 pb-12 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              Our <span className="text-teal-600">Medical Equipment</span> Catalog
            </h1>
            <p className="text-lg text-slate-600">
              Explore our comprehensive range of certified refurbished medical imaging equipment from leading manufacturers.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-white border-b border-slate-200">
        <div className="container mx-auto">
          <div className="flex items-center space-x-3 mb-4">
            <Filter className="text-slate-600" size={20} />
            <span className="text-sm font-medium text-slate-700">Filter by Category:</span>
          </div>
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <Button
                key={category.id}
                onClick={() => setSelectedCategory(category.key)}
                variant={selectedCategory === category.key ? 'default' : 'outline'}
                className={`transition-colors ${
                  selectedCategory === category.key
                    ? 'bg-teal-600 hover:bg-teal-700 text-white'
                    : 'border-slate-300 hover:border-teal-500 hover:text-teal-600'
                }`}
              >
                {category.name}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment Models Grid */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              {selectedCategory === 'all' ? 'All Equipment' : categories.find(c => c.key === selectedCategory)?.name}
            </h2>
            <p className="text-slate-600">{filteredModels.length} models available</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredModels.map((model) => (
              <Card key={model.id} className="border-slate-200 hover:border-teal-300 hover:shadow-xl transition-all duration-300 overflow-hidden group">
                <div className="relative h-48 overflow-hidden bg-slate-100">
                  <img
                    src={model.image}
                    alt={model.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute top-3 right-3">
                    <Badge className={`${
                      model.availability === 'Available' || model.availability === 'In Stock'
                        ? 'bg-green-500'
                        : 'bg-orange-500'
                    } text-white`}>
                      {model.availability}
                    </Badge>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-lg">{model.name}</CardTitle>
                  <CardDescription className="text-sm">{model.manufacturer}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Category:</span>
                      <span className="font-medium text-slate-900">{model.category}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Year:</span>
                      <span className="font-medium text-slate-900">{model.year}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Condition:</span>
                      <span className="font-medium text-teal-600">{model.condition}</span>
                    </div>
                  </div>

                  <div className="border-t border-slate-200 pt-3">
                    <p className="text-xs font-semibold text-slate-700 mb-2">Specifications:</p>
                    <div className="space-y-1">
                      {Object.entries(model.specifications).map(([key, value]) => (
                        <div key={key} className="flex justify-between text-xs">
                          <span className="text-slate-600 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                          <span className="font-medium text-slate-900">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button
                    onClick={() => handleInquiry(model)}
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white transition-colors"
                  >
                    Request Quote
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Brochures Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Download Brochures</h2>
            <p className="text-lg text-slate-600">
              Get detailed technical specifications and features of our equipment categories.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockBrochures.map((brochure) => (
              <Card key={brochure.id} className="border-slate-200 hover:border-teal-300 hover:shadow-lg transition-all">
                <CardHeader>
                  <div className="w-14 h-14 bg-teal-50 rounded-lg flex items-center justify-center mb-3">
                    <FileText className="text-teal-600" size={28} />
                  </div>
                  <CardTitle className="text-lg">{brochure.title}</CardTitle>
                  <CardDescription className="text-sm">{brochure.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2 text-xs text-slate-600">
                    <div className="flex items-center space-x-2">
                      <Calendar size={14} />
                      <span>Updated: {new Date(brochure.uploadDate).toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Size: {brochure.fileSize}</span>
                      <span>Pages: {brochure.pages}</span>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleDownloadBrochure(brochure.id, brochure.title)}
                    variant="outline"
                    className="w-full border-teal-600 text-teal-600 hover:bg-teal-50 transition-colors group"
                  >
                    <Download className="mr-2 group-hover:translate-y-0.5 transition-transform" size={16} />
                    Download PDF
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Upload Notice */}
          <div className="mt-12 bg-teal-50 border border-teal-200 rounded-xl p-6 text-center">
            <h3 className="font-bold text-slate-900 mb-2">Need Custom Specifications?</h3>
            <p className="text-slate-700 text-sm">
              Contact our team to receive detailed specifications tailored to your facility's requirements.
            </p>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ProductsPage;